// src/components/dashboard/ProfileComponent.jsx
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { TrashIcon, ExclamationTriangleIcon, GlobeAltIcon, CheckCircleIcon, XCircleIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { getProfile, updateProfile, deleteAccount as deleteAccountApi, updateLanguage, checkPasswordStrength } from '../../services/api';

function ProfileComponent({ institutionName }) {
   const { t, i18n } = useTranslation();
   
   // Set page title
   useEffect(() => {
      document.title = `${institutionName || t('profile.defaultInstitution')} - ${t('navigation.profile')}`;
   }, [institutionName, t]);

   const [profile, setProfile] = useState(null);
   const [institutions, setInstitutions] = useState([]);
   const [currentInstitution, setCurrentInstitution] = useState(null);
   const [isLoading, setIsLoading] = useState(true);
   const [error, setError] = useState('');

   // State for language
   const [selectedLanguage, setSelectedLanguage] = useState(i18n.language || 'en');
   const [isUpdatingLanguage, setIsUpdatingLanguage] = useState(false);
   const [languageUpdateSuccess, setLanguageUpdateSuccess] = useState('');
   const [languageUpdateError, setLanguageUpdateError] = useState('');

   // State for password update form
   const [currentPasswordInput, setCurrentPasswordInput] = useState('');
   const [newPasswordInput, setNewPasswordInput] = useState('');
   const [confirmNewPasswordInput, setConfirmNewPasswordInput] = useState('');
   const [showNewPassword, setShowNewPassword] = useState(false);
   const [showConfirmPassword, setShowConfirmPassword] = useState(false);
   const [passwordStrength, setPasswordStrength] = useState(null);
   const [isCheckingStrength, setIsCheckingStrength] = useState(false);
   const [isUpdating, setIsUpdating] = useState(false);
   const [updateError, setUpdateError] = useState('');
   const [updateSuccess, setUpdateSuccess] = useState('');

   // State for account deletion
   const [isDeleting, setIsDeleting] = useState(false);
   const [deleteError, setDeleteError] = useState('');
   const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
   const [deletePasswordInput, setDeletePasswordInput] = useState('');

   useEffect(() => {
       const fetchProfileData = async () => {
           setIsLoading(true);
           setError('');
           try {
               const response = await getProfile();
               if (response.success && response.data) {
                   setProfile(response.data.user);
                   setInstitutions(response.data.institutions || []);
                   
                   // Set language from user profile
                   if (response.data.user?.language) {
                       setSelectedLanguage(response.data.user.language);
                       // Update i18n language if different
                       if (i18n.language !== response.data.user.language) {
                           i18n.changeLanguage(response.data.user.language);
                       }
                   }
                   
                   // If currentInstitution is null, find it from institutions array
                   let currentInst = response.data.currentInstitution;
                   if (!currentInst && response.data.institutions?.length > 0) {
                       const currentInstitutionId = localStorage.getItem('currentInstitutionId');
                       currentInst = response.data.institutions.find(inst => 
                           inst.institution_id.toString() === currentInstitutionId
                       );
                   }
                   
                   setCurrentInstitution(currentInst);
               } else {
                   setError(response.message || t('profile.errors.loadFailed'));
               }
           } catch (err) {
               setError(err.message || t('profile.errors.loadError'));
               console.error("Fetch profile error:", err);
           } finally {
               setIsLoading(false);
           }
       };

       fetchProfileData();
   }, [t, i18n]);

   // Check password strength with debounce
   useEffect(() => {
       if (!newPasswordInput) {
           setPasswordStrength(null);
           return;
       }

       const timer = setTimeout(async () => {
           setIsCheckingStrength(true);
           try {
               const response = await checkPasswordStrength(newPasswordInput);
               if (response.success) {
                   setPasswordStrength(response.data);
               }
           } catch (error) {
               console.error('Error checking password strength:', error);
           } finally {
               setIsCheckingStrength(false);
           }
       }, 300);

       return () => clearTimeout(timer);
   }, [newPasswordInput]);

   const handleLanguageChange = async (newLanguage) => {
       if (newLanguage === selectedLanguage) return;

       setLanguageUpdateError('');
       setLanguageUpdateSuccess('');
       setIsUpdatingLanguage(true);

       try {
           const response = await updateLanguage(newLanguage);
           if (response.success) {
               setSelectedLanguage(newLanguage);
               // Update i18n immediately
               await i18n.changeLanguage(newLanguage);
               setLanguageUpdateSuccess(t('profile.language.updateSuccess'));
               
               // Store in localStorage for persistence
               localStorage.setItem('userLanguage', newLanguage);
               
               // Clear success message after 3 seconds
               setTimeout(() => {
                   setLanguageUpdateSuccess('');
               }, 3000);
           } else {
               setLanguageUpdateError(response.message || t('profile.language.errors.updateFailed'));
           }
       } catch (err) {
           setLanguageUpdateError(err.message || t('profile.language.errors.updateError'));
           console.error("Update language error:", err);
       } finally {
           setIsUpdatingLanguage(false);
       }
   };

   const meetsRequirement = (requirement) => {
       if (!newPasswordInput) return false;
       
       switch (requirement) {
           case 'length':
               return newPasswordInput.length >= 10;
           case 'lowercase':
               return /[a-z]/.test(newPasswordInput);
           case 'uppercase':
               return /[A-Z]/.test(newPasswordInput);
           case 'number':
               return /[0-9]/.test(newPasswordInput);
           case 'special':
               return /[!@#$%^&*(),.?":{}|<>]/.test(newPasswordInput);
           default:
               return false;
       }
   };

   const validatePassword = () => {
       const errors = [];
       
       if (!newPasswordInput || newPasswordInput.length < 10) {
           errors.push(t('profile.password.validation.minLength'));
       }
       
       if (!/[a-z]/.test(newPasswordInput)) {
           errors.push(t('profile.password.validation.lowercase'));
       }
       
       if (!/[A-Z]/.test(newPasswordInput)) {
           errors.push(t('profile.password.validation.uppercase'));
       }
       
       if (!/[0-9]/.test(newPasswordInput)) {
           errors.push(t('profile.password.validation.number'));
       }
       
       if (!/[!@#$%^&*(),.?":{}|<>]/.test(newPasswordInput)) {
           errors.push(t('profile.password.validation.special'));
       }
       
       return errors;
   };

   const handlePasswordUpdateSubmit = async (e) => {
       e.preventDefault();
       setUpdateError('');
       setUpdateSuccess('');

       // Validate password requirements
       const validationErrors = validatePassword();
       if (validationErrors.length > 0) {
           setUpdateError(validationErrors.join('. ') + '.');
           return;
       }

       if (newPasswordInput !== confirmNewPasswordInput) {
           setUpdateError(t('profile.password.errors.passwordMismatch'));
           return;
       }

       if (!currentPasswordInput) {
           setUpdateError(t('profile.password.errors.currentPasswordRequired'));
           return;
       }

       setIsUpdating(true);
       const updateData = {
           currentPassword: currentPasswordInput,
           newPassword: newPasswordInput,
       };

       try {
           const response = await updateProfile(updateData);
           if (response.success) {
               setUpdateSuccess(response.message || t('profile.password.updateSuccess'));
               setCurrentPasswordInput('');
               setNewPasswordInput('');
               setConfirmNewPasswordInput('');
               setPasswordStrength(null);
           } else {
               setUpdateError(response.message || t('profile.password.errors.updateFailed'));
           }
       } catch (err) {
           setUpdateError(err.message || t('profile.password.errors.updateError'));
           console.error("Update profile error:", err);
       } finally {
           setIsUpdating(false);
       }
   };

   const handleDeleteAccountInitiate = () => {
       setDeleteError('');
       setDeletePasswordInput('');
       setShowDeleteConfirmModal(true);
   };

   const handleDeleteAccountCancel = () => {
       setShowDeleteConfirmModal(false);
       setDeletePasswordInput('');
       setDeleteError('');
   };

   const handleDeleteAccountConfirm = async () => {
       if (!deletePasswordInput) {
           setDeleteError(t('profile.delete.errors.passwordRequired'));
           return;
       }

       setIsDeleting(true);
       try {
           const response = await deleteAccountApi(deletePasswordInput);
           if (response.success) {
               localStorage.removeItem('authToken');
               localStorage.removeItem('userData');
               window.location.href = '/login';
           } else {
               // Check for specific error codes or messages
               if (response.message === 'Validation failed. Please check your input.' || 
                   response.errorCode === 'VALIDATION_ERROR' ||
                   response.errorCode === 'INCORRECT_PASSWORD_FOR_DELETE_C2') {
                   setDeleteError(t('profile.delete.errors.incorrectPassword'));
               } else {
                   setDeleteError(response.message || t('profile.delete.errors.deleteFailed'));
               }
           }
       } catch (err) {
           // Handle validation errors in catch block too
           if (err.data?.message === 'Validation failed. Please check your input.' ||
               err.data?.errorCode === 'VALIDATION_ERROR' ||
               err.data?.errorCode === 'INCORRECT_PASSWORD_FOR_DELETE_C2') {
               setDeleteError(t('profile.delete.errors.incorrectPassword'));
           } else {
               setDeleteError(err.data?.message || err.message || t('profile.delete.errors.deleteError'));
           }
           console.error("Delete account error:", err);
       } finally {
           setIsDeleting(false);
       }
   };

   const getStrengthColor = (strength) => {
       switch (strength) {
           case 'strong': return 'text-green-600 bg-green-50 border-green-200';
           case 'good': return 'text-blue-600 bg-blue-50 border-blue-200';
           case 'fair': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
           case 'weak': return 'text-red-600 bg-red-50 border-red-200';
           default: return 'text-gray-600 bg-gray-50 border-gray-200';
       }
   };

   const getStrengthBarColor = (strength) => {
       switch (strength) {
           case 'strong': return 'bg-green-500';
           case 'good': return 'bg-blue-500';
           case 'fair': return 'bg-yellow-500';
           case 'weak': return 'bg-red-500';
           default: return 'bg-gray-300';
       }
   };

   if (isLoading) {
       return <div className="p-6 text-center">{t('profile.loadingProfile')}</div>;
   }

   if (error) {
       return (
           <div className="p-6 text-red-600 bg-red-100 border border-red-400 rounded-xl">
               {t('common.error')}: {error}
           </div>
       );
   }

   if (!profile) {
       return <div className="p-6">{t('profile.noProfileData')}</div>;
   }

   const passwordMinLength = import.meta.env.VITE_PASSWORD_MIN_LENGTH || 10;

   const languageOptions = [
       { value: 'en', label: 'English', flag: '🇬🇧' },
       { value: 'nl', label: 'Nederlands', flag: '🇳🇱' },
       { value: 'de', label: 'Deutsch', flag: '🇩🇪' },
       { value: 'fr', label: 'Français', flag: '🇫🇷' }
   ];

   return (
       <div className="h-full overflow-y-auto">
           <div className="space-y-8 p-4 md:p-6 max-w-4xl mx-auto">
               {/* Profile Information Section */}
               <section className="p-6 bg-white shadow-lg rounded-xl border border-slate-200">
                   <h2 className="text-2xl font-semibold text-slate-800 mb-6">{t('profile.myProfile')}</h2>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                       <div>
                           <label className="block text-sm font-medium text-slate-500">
                               {t('profile.displayName')}
                           </label>
                           <p className="mt-1 text-lg text-slate-700">
                               {currentInstitution?.display_name || t('profile.notSet')}
                           </p>
                           {currentInstitution && (
                               <p className="text-xs text-slate-500 mt-1">
                                   {t('profile.at')} {currentInstitution.name}
                               </p>
                           )}
                       </div>
                       <div>
                           <label className="block text-sm font-medium text-slate-500">
                               {t('profile.emailAddress')}
                           </label>
                           <p className="mt-1 text-lg text-slate-700">
                               {profile.email}{' '}
                               {profile.email_verified ? (
                                   <span className="text-xs text-green-600">
                                       ({t('profile.verified')})
                                   </span>
                               ) : (
                                   <span className="text-xs text-orange-600">
                                       ({t('profile.notVerified')})
                                   </span>
                               )}
                           </p>
                       </div>
                       <div>
                           <label className="block text-sm font-medium text-slate-500">
                               {t('profile.currentRole')}
                           </label>
                           <p className="mt-1 text-lg text-slate-700 capitalize">
                               {currentInstitution?.role_in_institution 
                                   ? t(`profile.roles.${currentInstitution.role_in_institution}`)
                                   : t('profile.noRole')
                               }
                           </p>
                       </div>
                       <div>
                           <label className="block text-sm font-medium text-slate-500">
                               {t('profile.accountCreated')}
                           </label>
                           <p className="mt-1 text-lg text-slate-700">
                               {profile.created_at 
                                   ? new Date(profile.created_at).toLocaleDateString(
                                       { en: 'en-US', nl: 'nl-NL', de: 'de-DE', fr: 'fr-FR' }[i18n.language] || 'en-US'
                                   ) 
                                   : t('profile.invalidDate')
                               }
                           </p>
                       </div>
                   </div>
               </section>

               {/* All Institutions Section */}
               <section className="p-6 bg-white shadow-lg rounded-xl border border-slate-200">
                   <div className="mb-6">
                       <h2 className="text-xl font-semibold text-slate-800">
                           {t('profile.institutions.title')}
                       </h2>
                       <p className="text-sm text-slate-500 mt-1">
                           {t('profile.institutions.description')}
                       </p>
                   </div>

                   {institutions.length > 0 ? (
                       <div className="space-y-3">
                           {institutions.map((inst, index) => (
                               <div 
                                   key={inst.institution_id} 
                                   className={`p-4 rounded-lg border ${
                                       currentInstitution?.institution_id === inst.institution_id 
                                           ? 'border-cyan-500 bg-cyan-50' 
                                           : 'border-slate-200 bg-white'
                                   } transition-all duration-200`}
                               >
                                   <div className="flex items-center justify-between">
                                       <div className="flex-1">
                                           <div className="flex items-center space-x-3">
                                               <div>
                                                   <h3 className="text-lg font-medium text-slate-800">
                                                       {inst.name}
                                                   </h3>
                                                   <p className="text-sm text-slate-600 mt-0.5">
                                                       {t('profile.institutions.displayName')}:{' '}
                                                       <span className="font-medium">
                                                           {inst.display_name || t('profile.notSet')}
                                                       </span>
                                                   </p>
                                                   <div className="flex items-center mt-2 space-x-2">
                                                       <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                           inst.role_in_institution === 'teacher' 
                                                               ? 'bg-purple-100 text-purple-800'
                                                               : inst.role_in_institution === 'student'
                                                               ? 'bg-green-100 text-green-800'
                                                               : 'bg-gray-100 text-gray-800'
                                                       }`}>
                                                           {t(`profile.roles.${inst.role_in_institution || 'member'}`)}
                                                       </span>
                                                   </div>
                                               </div>
                                           </div>
                                       </div>
                                       {currentInstitution?.institution_id === inst.institution_id && (
                                           <div className="ml-4">
                                               <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
                                                   {t('profile.institutions.current')}
                                               </span>
                                           </div>
                                       )}
                                   </div>
                               </div>
                           ))}
                       </div>
                   ) : (
                       <div className="text-center py-8 text-slate-500">
                           <p>{t('profile.institutions.noAccess')}</p>
                       </div>
                   )}
               </section>

               {/* Language Preference Section */}
               <section className="p-6 bg-white shadow-lg rounded-xl border border-slate-200">
                   <div className="flex items-center space-x-3 mb-4">
                       <GlobeAltIcon className="h-6 w-6 text-blue-600" />
                       <div>
                           <h2 className="text-xl font-semibold text-slate-800">
                               {t('profile.language.title')}
                           </h2>
                           <p className="text-sm text-slate-500 mt-1">
                               {t('profile.language.description')}
                           </p>
                       </div>
                   </div>

                   {languageUpdateSuccess && (
                       <p className="text-sm text-green-600 p-3 bg-green-50 border border-green-300 rounded-md mb-4">
                           {languageUpdateSuccess}
                       </p>
                   )}
                   {languageUpdateError && (
                       <p className="text-sm text-red-600 p-3 bg-red-50 border border-red-300 rounded-md mb-4">
                           {languageUpdateError}
                       </p>
                   )}

                   <div className="max-w-xs">
                       <label htmlFor="language-select" className="block text-sm font-medium text-slate-700 mb-2">
                           {t('profile.language.selectLanguage')}
                       </label>
                       <div className="relative">
                           <select
                               id="language-select"
                               value={selectedLanguage}
                               onChange={(e) => handleLanguageChange(e.target.value)}
                               disabled={isUpdatingLanguage}
                               className="block w-full px-4 py-2.5 pr-10 bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed appearance-none"
                           >
                               {languageOptions.map(option => (
                                   <option key={option.value} value={option.value}>
                                       {option.flag} {option.label}
                                   </option>
                               ))}
                           </select>
                           <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-slate-700">
                               <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                   <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                               </svg>
                           </div>
                       </div>
                       {isUpdatingLanguage && (
                           <p className="text-xs text-slate-500 mt-2">
                               {t('profile.language.updating')}
                           </p>
                       )}
                   </div>
               </section>

               {/* Update Password Section */}
               <section className="p-6 bg-white shadow-lg rounded-xl border border-slate-200">
                   <h2 className="text-xl font-semibold text-slate-800 mb-1">
                       {t('profile.password.title')}
                   </h2>
                   <p className="text-sm text-slate-500 mb-5">
                       {t('profile.password.description')}
                   </p>
                   <form onSubmit={handlePasswordUpdateSubmit} className="space-y-4">
                       {updateError && (
                           <p className="text-sm text-red-600 p-3 bg-red-50 border border-red-300 rounded-md">
                               {updateError}
                           </p>
                       )}
                       {updateSuccess && (
                           <p className="text-sm text-green-600 p-3 bg-green-50 border border-green-300 rounded-md">
                               {updateSuccess}
                           </p>
                       )}
                       
                       <div>
                           <label htmlFor="currentPasswordInput" className="block text-sm font-medium text-slate-700 mb-1.5">
                               {t('profile.password.currentPassword')}
                           </label>
                           <input
                               id="currentPasswordInput"
                               type="password"
                               value={currentPasswordInput}
                               onChange={(e) => setCurrentPasswordInput(e.target.value)}
                               className="block w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                               placeholder={t('profile.password.currentPasswordPlaceholder')}
                               disabled={isUpdating}
                               autoComplete="current-password"
                           />
                       </div>
                       
                       <div>
                           <label htmlFor="newPasswordInput" className="block text-sm font-medium text-slate-700 mb-1.5">
                               {t('profile.password.newPassword')}
                           </label>
                           <div className="relative">
                               <input
                                   id="newPasswordInput"
                                   type={showNewPassword ? 'text' : 'password'}
                                   value={newPasswordInput}
                                   onChange={(e) => setNewPasswordInput(e.target.value)}
                                   className="block w-full px-4 py-2.5 pr-12 bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                                   placeholder={t('profile.password.newPasswordPlaceholder', { length: passwordMinLength })}
                                   disabled={isUpdating}
                                   autoComplete="new-password"
                               />
                               <button
                                   type="button"
                                   onClick={() => setShowNewPassword(!showNewPassword)}
                                   className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                                   tabIndex={-1}
                               >
                                   {showNewPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                               </button>
                           </div>

                           {/* Password Requirements */}
                           {newPasswordInput && (
                               <div className="mt-3 space-y-2">
                                   {/* Strength Bar */}
                                   {passwordStrength && !isCheckingStrength && (
                                       <>
                                           <div className="flex gap-1">
                                               {[25, 50, 75, 100].map((threshold) => (
                                                   <div
                                                       key={threshold}
                                                       className={`h-1.5 flex-1 rounded-full transition-colors ${
                                                           passwordStrength.score >= threshold 
                                                               ? getStrengthBarColor(passwordStrength.strength)
                                                               : 'bg-gray-200'
                                                       }`}
                                                   />
                                               ))}
                                           </div>
                                           
                                           <div className={`text-xs font-medium px-2 py-1 rounded border inline-block ${
                                               getStrengthColor(passwordStrength.strength)
                                           }`}>
                                               {t(`profile.password.strength.${passwordStrength.strength}`)}
                                           </div>
                                       </>
                                   )}

                                   {/* Requirements Checklist */}
                                   <div className="space-y-1 text-xs">
                                       <div className={`flex items-center ${
                                           meetsRequirement('length') ? 'text-green-600' : 'text-gray-400'
                                       }`}>
                                           {meetsRequirement('length') ? (
                                               <CheckCircleIcon className="h-4 w-4 mr-1" />
                                           ) : (
                                               <XCircleIcon className="h-4 w-4 mr-1" />
                                           )}
                                           {t('profile.password.requirements.minLength')}
                                       </div>
                                       <div className={`flex items-center ${
                                           meetsRequirement('lowercase') ? 'text-green-600' : 'text-gray-400'
                                       }`}>
                                           {meetsRequirement('lowercase') ? (
                                               <CheckCircleIcon className="h-4 w-4 mr-1" />
                                           ) : (
                                               <XCircleIcon className="h-4 w-4 mr-1" />
                                           )}
                                           {t('profile.password.requirements.lowercase')}
                                       </div>
                                       <div className={`flex items-center ${
                                           meetsRequirement('uppercase') ? 'text-green-600' : 'text-gray-400'
                                       }`}>
                                           {meetsRequirement('uppercase') ? (
                                               <CheckCircleIcon className="h-4 w-4 mr-1" />
                                           ) : (
                                               <XCircleIcon className="h-4 w-4 mr-1" />
                                           )}
                                           {t('profile.password.requirements.uppercase')}
                                       </div>
                                       <div className={`flex items-center ${
                                           meetsRequirement('number') ? 'text-green-600' : 'text-gray-400'
                                       }`}>
                                           {meetsRequirement('number') ? (
                                               <CheckCircleIcon className="h-4 w-4 mr-1" />
                                           ) : (
                                               <XCircleIcon className="h-4 w-4 mr-1" />
                                           )}
                                           {t('profile.password.requirements.number')}
                                       </div>
                                       <div className={`flex items-center ${
                                           meetsRequirement('special') ? 'text-green-600' : 'text-gray-400'
                                       }`}>
                                           {meetsRequirement('special') ? (
                                               <CheckCircleIcon className="h-4 w-4 mr-1" />
                                           ) : (
                                               <XCircleIcon className="h-4 w-4 mr-1" />
                                           )}
                                           {t('profile.password.requirements.special')}
                                       </div>
                                   </div>
                               </div>
                           )}
                       </div>

                       <div>
                           <label htmlFor="confirmNewPasswordInput" className="block text-sm font-medium text-slate-700 mb-1.5">
                               {t('profile.password.confirmNewPassword')}
                           </label>
                           <div className="relative">
                               <input
                                   id="confirmNewPasswordInput"
                                   type={showConfirmPassword ? 'text' : 'password'}
                                   value={confirmNewPasswordInput}
                                   onChange={(e) => setConfirmNewPasswordInput(e.target.value)}
                                   className="block w-full px-4 py-2.5 pr-12 bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                                   placeholder={t('profile.password.confirmNewPasswordPlaceholder')}
                                   disabled={isUpdating}
                                   autoComplete="new-password"
                               />
                               <button
                                   type="button"
                                   onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                   className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                                   tabIndex={-1}
                               >
                                   {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                               </button>
                           </div>

                           {confirmNewPasswordInput && (
                               <div className={`mt-1 text-xs flex items-center ${
                                   newPasswordInput === confirmNewPasswordInput ? 'text-green-600' : 'text-red-600'
                               }`}>
                                   {newPasswordInput === confirmNewPasswordInput ? (
                                       <>
                                           <CheckCircleIcon className="h-4 w-4 mr-1" />
                                           {t('profile.password.passwordsMatch')}
                                       </>
                                   ) : (
                                       <>
                                           <XCircleIcon className="h-4 w-4 mr-1" />
                                           {t('profile.password.passwordsNoMatch')}
                                       </>
                                   )}
                               </div>
                           )}
                       </div>

                       <button
                           type="submit"
                           disabled={isUpdating}
                           className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                       >
                           {isUpdating ? t('profile.password.updating') : t('profile.password.updateButton')}
                       </button>
                   </form>
               </section>

               {/* Account Deletion Section - GDPR Compliance */}
               <section className="p-6 bg-white shadow-lg rounded-xl border border-red-200">
                   <div className="flex items-start space-x-3 mb-4">
                       <TrashIcon className="h-6 w-6 text-red-600 mt-0.5 flex-shrink-0" />
                       <div>
                           <h2 className="text-xl font-semibold text-red-800 mb-1">
                               {t('profile.delete.title')}
                           </h2>
                           <p className="text-sm text-red-600 mb-3">
                               {t('profile.delete.description')}
                           </p>
                       </div>
                   </div>

                   <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                       <div className="flex items-start space-x-3">
                           <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                           <div className="text-sm text-red-800">
                               <p className="font-medium mb-2">
                                   {t('profile.delete.warning')}
                               </p>
                               <ul className="list-disc list-inside space-y-1 text-red-700">
                                   <li>
                                       {t('profile.delete.consequences.institutions', {
                                           count: institutions.length
                                       })}
                                   </li>
                                   <li>{t('profile.delete.consequences.data')}</li>
                                   <li>{t('profile.delete.consequences.access')}</li>
                                   <li>{t('profile.delete.consequences.gdpr')}</li>
                               </ul>
                           </div>
                       </div>
                   </div>

                   <button
                       onClick={handleDeleteAccountInitiate}
                       className="px-6 py-2.5 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition duration-200"
                   >
                       {t('profile.delete.button')}
                   </button>
               </section>

               {/* Delete Confirmation Modal with Portal */}
               {showDeleteConfirmModal && ReactDOM.createPortal(
                   <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-50">
                       <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl relative z-[10000]">
                           <div className="flex items-center space-x-3 mb-4">
                               <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
                               <h3 className="text-lg font-semibold text-slate-800">
                                   {t('profile.delete.modal.title')}
                               </h3>
                           </div>

                           <p className="text-sm text-slate-600 mb-4">
                               {t('profile.delete.modal.description', {
                                   count: institutions.length
                               })}
                           </p>

                           {deleteError && (
                               <p className="text-sm text-red-600 p-3 bg-red-50 border border-red-300 rounded-md mb-4">
                                   {deleteError}
                               </p>
                           )}

                           <input
                               type="password"
                               value={deletePasswordInput}
                               onChange={(e) => setDeletePasswordInput(e.target.value)}
                               className="block w-full px-4 py-2.5 bg-slate-50 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition duration-200 mb-4"
                               placeholder={t('profile.delete.modal.passwordPlaceholder')}
                               disabled={isDeleting}
                               autoFocus
                           />

                           <div className="flex space-x-3">
                               <button
                                   onClick={handleDeleteAccountCancel}
                                   disabled={isDeleting}
                                   className="flex-1 px-4 py-2.5 bg-gray-200 text-gray-800 font-medium rounded-xl hover:bg-gray-300 focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                               >
                                   {t('profile.delete.modal.cancel')}
                               </button>
                               <button
                                   onClick={handleDeleteAccountConfirm}
                                   disabled={isDeleting}
                                   className="flex-1 px-4 py-2.5 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                               >
                                   {isDeleting ? t('profile.delete.modal.deleting') : t('profile.delete.modal.deleteButton')}
                               </button>
                           </div>
                       </div>
                   </div>,
                   document.body
               )}
           </div>
       </div>
   );
}

export default ProfileComponent;